import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Plus, Phone, Mail, Globe, ChevronRight } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { SkeletonCard } from '../components/ui/Skeleton';
import { EmptySuppliers, ErrorState } from '../components/ui/EmptyState';
import AddSupplierModal from '../components/suppliers/AddSupplierModal';
import { useSuppliers } from '../hooks/useSuppliers';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const Suppliers = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { data: suppliers = [], isLoading: loading, error, refetch } = useSuppliers();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredSuppliers = suppliers.filter((supplier) => {
        const term = searchTerm.toLowerCase();
        return (
            supplier.name?.toLowerCase().includes(term) ||
            supplier.description?.toLowerCase().includes(term) ||
            supplier.phone?.toLowerCase().includes(term) ||
            supplier.email?.toLowerCase().includes(term) ||
            supplier.website?.toLowerCase().includes(term)
        );
    });

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-text-primary">
                        {t(KEYS.SUPPLIERS_TITLE)}
                    </h1>
                    <p className="text-sm text-text-muted mt-0.5">
                        {t(KEYS.SUPPLIERS_SUBTITLE)}
                    </p>
                </div>
                <Button 
                    variant="primary" 
                    onClick={() => setIsModalOpen(true)}
                    className="gap-2"
                    size="small"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {t(KEYS.SUPPLIERS_ADD)}
                </Button>
            </div>

            {/* Search Filter */}
            <div className="relative">
                <input
                    type="text"
                    placeholder={t(KEYS.SUPPLIERS_SEARCH_PLACEHOLDER || 'Search suppliers...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-80 pl-9 pr-4 py-2 text-sm bg-surface-primary border border-surface-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent transition-colors"
                />
                <svg
                    className="absolute left-3 top-2.5 w-4 h-4 text-text-muted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                </svg>
            </div>

            {/* Content */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <SkeletonCard key={i} />
                    ))}
                </div>
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.SUPPLIERS_ERROR_LOAD)} onRetry={refetch} />
            ) : filteredSuppliers && filteredSuppliers.length > 0 ? (
                <Card padding="none">
                    <div className="divide-y divide-surface-border">
                        {filteredSuppliers.map((supplier, index) => (
                            <div
                                key={supplier.uid}
                                className="flex items-center justify-between px-4 py-3 hover:bg-surface-overlay transition-colors cursor-pointer group animate-appear"
                                style={{ animationDelay: `${index * 50}ms` }}
                                onClick={() => navigate(`/suppliers/${supplier.uid}`)}
                            >
                                <div className="flex-1 min-w-0 mr-4">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-sm font-medium text-text-primary group-hover:text-accent transition-colors truncate">
                                            {supplier.name}
                                        </h3>
                                        {supplier.description && (
                                            <span className="text-xs text-text-muted truncate hidden sm:inline">
                                                {supplier.description}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1">
                                        {supplier.phone && (
                                            <span className="text-xs text-text-muted flex items-center gap-1">
                                                <Phone className="w-3 h-3" />
                                                {supplier.phone}
                                            </span>
                                        )}
                                        {supplier.email && (
                                            <span className="text-xs text-text-muted flex items-center gap-1 hidden sm:flex">
                                                <Mail className="w-3 h-3" />
                                                {supplier.email}
                                            </span>
                                        )}
                                        {supplier.website && (
                                            <span className="text-xs text-text-muted flex items-center gap-1 hidden md:flex">
                                                <Globe className="w-3 h-3" />
                                                {supplier.website}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-accent group-hover:translate-x-0.5 transition-all shrink-0" />
                            </div>
                        ))}
                    </div>
                </Card>
            ) : (
                <EmptySuppliers onAdd={() => setIsModalOpen(true)} />
            )}

            <AddSupplierModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
};

export default Suppliers;
